'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { useSafeFeatureFlagVariantKey } from 'hooks/useSafeFeatureFlag';
import { useSe3QaSearch } from 'components/HomepageV3/utils/routes';

export const HOMEPAGE_TOMBSTONE_EXPERIMENT_FLAG = 'homepage-tombstone-card-abc-test';
export const HOMEPAGE_TOMBSTONE_OVERRIDE_PARAM = 'tombstone_variant';

export type HomepageTombstoneVariant = 'control' | 'marketplace-overlay' | 'hybrid';

export function parseHomepageTombstoneVariant(
  value: string | boolean | null | undefined,
): HomepageTombstoneVariant | null {
  if (typeof value !== 'string') return null;
  switch (value.trim().toLowerCase()) {
    case 'control':
    case 'a':
      return 'control';
    case 'marketplace-overlay':
    case 'marketplace':
    case 'b':
      return 'marketplace-overlay';
    case 'hybrid':
    case 'c':
      return 'hybrid';
    default:
      return null;
  }
}

export function resolveHomepageTombstoneVariant(
  search: string,
  featureFlagVariant: string | boolean | null | undefined,
): HomepageTombstoneVariant {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return (
    parseHomepageTombstoneVariant(params.get(HOMEPAGE_TOMBSTONE_OVERRIDE_PARAM)) ??
    parseHomepageTombstoneVariant(featureFlagVariant) ??
    'control'
  );
}

export function useHomepageTombstoneVariant({
  sendExposure = true,
}: {
  sendExposure?: boolean;
} = {}): HomepageTombstoneVariant {
  const search = useSe3QaSearch();
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const override = parseHomepageTombstoneVariant(params.get(HOMEPAGE_TOMBSTONE_OVERRIDE_PARAM));
  // Forced design-review URLs must not pollute experiment exposure data.
  const featureFlagVariant = useSafeFeatureFlagVariantKey(
    override ? '' : HOMEPAGE_TOMBSTONE_EXPERIMENT_FLAG,
    { sendEvent: sendExposure },
  );
  return override ?? resolveHomepageTombstoneVariant('', featureFlagVariant);
}

const HomepageTombstoneVariantContext = createContext<HomepageTombstoneVariant>('control');

export function HomepageTombstoneVariantProvider({
  value,
  children,
}: {
  value: HomepageTombstoneVariant;
  children: ReactNode;
}) {
  return (
    <HomepageTombstoneVariantContext.Provider value={value}>
      {children}
    </HomepageTombstoneVariantContext.Provider>
  );
}

export const useAssignedHomepageTombstoneVariant = () =>
  useContext(HomepageTombstoneVariantContext);
